/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Actions for history-middleware
 */

import type { matchPath } from '@zcode/router-utils'
import type { Dispatch as StateTypeDispatch, State as StateTypeState } from '@zcode/state-type'
import type { LocationDescriptorObject, LocationListener, LocationState, Path } from 'history'
import type { StaticContext } from 'react-router'
import type { RouteComponentProps } from 'react-router-dom'
import type { Dispatch as ReduxDispatch } from 'redux'
import { type Action, createAction } from 'redux-actions'

import {
  DELETE_HASH,
  DELETE_HASH_QUERY,
  DELETE_QUERY,
  GET_ROUTE_PROPS,
  GO,
  GO_BACK,
  GO_FORWARD,
  LISTEN,
  MATCH,
  PUSH,
  PUSH_HASH,
  PUSH_SEARCH,
  REPLACE,
  REPLACE_HASH,
  REPLACE_SEARCH,
  SET_HASH_QUERY,
  SET_QUERY,
  START_HISTORY_MIDDLEWARE,
  STOP_HISTORY_MIDDLEWARE,
  UNLISTEN,
} from './constants'

const noop = () => undefined
function pathPayload(
  location: LocationDescriptorObject<LocationState>
): [location: LocationDescriptorObject<LocationState>]
function pathPayload(
  path: Path,
  state?: LocationState,
  status?: number
): [path: Path, state?: LocationState, status?: number]
function pathPayload(
  path: LocationDescriptorObject<LocationState> | Path,
  state?: LocationState,
  status?: number
): [
  location: LocationDescriptorObject<LocationState> | Path,
  state?: LocationState,
  status?: number,
] {
  return typeof state === 'undefined' && typeof status === 'undefined'
    ? [path]
    : ([path, state, status] as const)
}

interface PathActionCreator<Type extends typeof PUSH | typeof REPLACE> {
  (
    location: LocationDescriptorObject<LocationState>
  ): Action<Type, [LocationDescriptorObject<LocationState>]>
  (path: Path, state?: LocationState): Action<Type, [Path, LocationState?]>
  (
    path: LocationDescriptorObject<LocationState> | Path,
    state?: LocationState,
    status?: number
  ): Action<
    Type,
    [LocationDescriptorObject<LocationState>] | [Path, LocationState?, status?: number]
  >
}

export type RouteProps<
  Params extends { [K in keyof Params]?: string } = { [K in string]?: string },
> = Omit<RouteComponentProps<Params, StaticContext, unknown>, 'match'> & {
  match: RouteComponentProps<Params, StaticContext, unknown>['match'] | null
}

export type RoutePropsWithoutMatch<
  Params extends { [K in keyof Params]?: string } = { [K in string]?: string },
> = Omit<RouteComponentProps<Params, StaticContext, unknown>, 'match'> & { match: null }

type MatchFunction<
  Return,
  State = StateTypeState,
  Dispatch extends ReduxDispatch = StateTypeDispatch,
  Params extends { [K in keyof Params]?: string } = { [K in string]?: string },
> = (routeProps: RouteProps<Params>, state: State, dispatch: Dispatch) => Return

export type Route = Parameters<typeof matchPath>[1]

export interface MatchAction<
  Return,
  State = StateTypeState,
  Dispatch extends ReduxDispatch = StateTypeDispatch,
> {
  payload: {
    func: MatchFunction<Return, State, Dispatch>
    routes: Route[]
  }
  type: typeof MATCH
}

const matchPayload = <
  Return,
  State = StateTypeState,
  Dispatch extends ReduxDispatch = StateTypeDispatch,
>(
  routes: Route[] | undefined,
  func: MatchFunction<Return, State, Dispatch>
) => ({ routes, func })

type MatchActionCreator = <
  Return,
  State = StateTypeState,
  Dispatch extends ReduxDispatch = StateTypeDispatch,
>(
  routes: Route[] | undefined,
  func: MatchFunction<Return, State, Dispatch>
) => MatchAction<Return, State, Dispatch>

export type GetRoutePropsAction<Payload extends { routes?: Route[] } | undefined> = Action<
  typeof GET_ROUTE_PROPS,
  Payload
>

interface GetRoutePropsActionCreator {
  (args?: { routes?: undefined }): GetRoutePropsAction<{ routes?: undefined } | undefined>
  (args: { routes: Route[] }): GetRoutePropsAction<{
    routes: Route[]
  }>
  <Payload extends { routes?: Route[] } | undefined>(args?: {
    routes?: Route[]
  }): GetRoutePropsAction<Payload>
}

export const getRouteProps = createAction<{ routes?: Route[] } | undefined, typeof GET_ROUTE_PROPS>(
  GET_ROUTE_PROPS
) as GetRoutePropsActionCreator

export const go = createAction<number, typeof GO>(GO)
export const goBack = createAction(GO_BACK, noop)
export const goForward = createAction(GO_FORWARD, noop)
export const match = createAction(MATCH, matchPayload) as MatchActionCreator
export const push = createAction(PUSH, pathPayload) as PathActionCreator<typeof PUSH>
export const replace = createAction(REPLACE, pathPayload) as PathActionCreator<typeof REPLACE>
export const listen = createAction<LocationListener<LocationState>, typeof LISTEN>(LISTEN)
export const startHistoryMiddleware = createAction(START_HISTORY_MIDDLEWARE)
export const stopHistoryMiddleware = createAction(STOP_HISTORY_MIDDLEWARE)
export const unlisten = createAction<LocationListener<LocationState>, typeof UNLISTEN>(UNLISTEN)
// hash query
export const deleteHash = createAction(DELETE_HASH)
export const deleteHashQuery = createAction<{ key?: string }, typeof DELETE_HASH_QUERY>(
  DELETE_HASH_QUERY
) // push
export const pushHash = createAction<{ hash?: string }, typeof PUSH_HASH>(PUSH_HASH)
export const replaceHash = createAction<{ hash?: string }, typeof REPLACE_HASH>(REPLACE_HASH)
export const setHashQuery = createAction<
  { key?: string; value?: unknown; replace?: boolean },
  typeof SET_HASH_QUERY
>(SET_HASH_QUERY) // replace or push
// search query
export const deleteQuery = createAction<{ key?: string }, typeof DELETE_QUERY>(DELETE_QUERY) // push
export const pushSearch = createAction<{ search?: string }, typeof PUSH_SEARCH>(PUSH_SEARCH)
export const replaceSearch = createAction<{ search?: string }, typeof REPLACE_SEARCH>(
  REPLACE_SEARCH
)
export const setQuery = createAction<
  { key?: string; value?: unknown; replace?: boolean },
  typeof SET_QUERY
>(SET_QUERY) // replace or push
